<script>
	import { onMount } from "svelte";
	import { browser } from "$app/environment";
	import { slugify } from "cando-util";

	import TextInput from "../TextInput.svelte";

	export let dataUrl;
	export let label;
	export let readonly;
	export let disabled;

	let canvas;
	let signaturePad; // Instance of SignaturePad
	let renderedDataUrl;
	let signByTyping = false;
	let signatureName = "";
	let prefersDark = false;

	onMount(async () => {
		const module = await import("signature_pad");
		const SignaturePad = module.default;

		if (browser) {
			prefersDark = window.matchMedia(
				"(prefers-color-scheme: dark)",
			).matches;
		}

		canvas.getContext("2d", { willReadFrequently: !readonly });

		signaturePad = new SignaturePad(canvas, {
			backgroundColor: "rgba(255, 255, 255, 0)", // transparent background; change as needed
			penColor: prefersDark ? "white" : "black", // pen color
		});

		signaturePad.addEventListener("endStroke", saveSignature);
	});

	const saveSignature = () => {
		dataUrl = getDrawnSignatureDataUrl();
		renderedDataUrl = dataUrl;
	};

	const getDrawnSignatureDataUrl = () => {
		if (!prefersDark) return signaturePad.toDataURL();

		const exportCanvas = document.createElement("canvas");
		exportCanvas.width = canvas.width;
		exportCanvas.height = canvas.height;
		const ctx = exportCanvas.getContext("2d", { willReadFrequently: true });

		ctx.drawImage(canvas, 0, 0, canvas.width, canvas.height);

		const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
		const data = imageData.data;

		for (let i = 0; i < data.length; i += 4) {
			const r = data[i];
			const g = data[i + 1];
			const b = data[i + 2];
			const alpha = data[i + 3];

			if (r > 200 && g > 200 && b > 200 && alpha > 0) {
				data[i] = 0;
				data[i + 1] = 0;
				data[i + 2] = 0;
			}
		}

		ctx.putImageData(imageData, 0, 0);
		return exportCanvas.toDataURL();
	};

	const clearPad = () => {
		signaturePad.clear();
		dataUrl = undefined;
		renderedDataUrl = dataUrl;
	};

	$: if (dataUrl && signaturePad) {
		if (renderedDataUrl !== dataUrl) {
			// let's get the canvas loaded up then.
			renderSignature(dataUrl);
			renderedDataUrl = dataUrl;
		}
	}

	const renderSignature = (dataUrl) => {
		signaturePad.clear();
		signaturePad.fromDataURL(dataUrl);

		if (!prefersDark) return;

		setTimeout(() => {
			const ctx = canvas.getContext("2d", { willReadFrequently: true });
			const imageData = ctx.getImageData(
				0,
				0,
				canvas.width,
				canvas.height,
			);
			const data = imageData.data;

			for (let i = 0; i < data.length; i += 4) {
				const r = data[i];
				const g = data[i + 1];
				const b = data[i + 2];
				const alpha = data[i + 3];

				if (r < 50 && g < 50 && b < 50 && alpha > 0) {
					data[i] = 255;
					data[i + 1] = 255;
					data[i + 2] = 255;
				}
			}
			ctx.putImageData(imageData, 0, 0);
		}, 100);
	};

	const loadScriptSignature = () => {
		signByTyping = !signByTyping;

		dataUrl = generateSignatureDataUrl(signatureName);

		renderSignature(dataUrl);
	};

	const generateSignatureDataUrl = (name) => {
		const tempCanvas = document.createElement("canvas");
		tempCanvas.width = 400;
		tempCanvas.height = 200;
		const ctx = tempCanvas.getContext("2d", { willReadFrequently: true });

		ctx.fillStyle = "black";
		ctx.font = "32px cursive";
		ctx.textAlign = "center";
		ctx.textBaseline = "middle";
		ctx.fillText(name, tempCanvas.width / 2, tempCanvas.height / 2);

		return tempCanvas.toDataURL("image/png");
	};

	$: if (signaturePad) {
		if (readonly || disabled) {
			signaturePad.off();
		} else {
			signaturePad.on();
		}
	}
</script>

<div class="max-w-96">
	{#if signByTyping}
		<div>
			<div>Type your name below to sign:</div>
			<div class="flex justify-between">
				<TextInput
					{disabled}
					cls={"input input-bordered input-primary rounded w-80"}
					bind:value={signatureName}
				/>
				<button
					{disabled}
					class="btn btn-sm hover-ripple"
					on:click|preventDefault={loadScriptSignature}
				>
					{`${signByTyping ? "Done" : "Sign By Typing"}`}
				</button>
			</div>
		</div>
	{/if}

	<div
		class={`input input-bordered input-primary rounded-xl relative w-full max-w-[400px] h-[200px] ${
			readonly || disabled ? "" : "hover:cursor-crosshair"
		}`}
	>
		<canvas
			id={`${slugify(label)}-signaturepad-canvas`}
			class="block absolute top-0 left-0 w-full h-full"
			bind:this={canvas}
			width="400"
			height="200"
			on:click|preventDefault|stopPropagation
		></canvas>
	</div>

	{#if !readonly}
		<div class="mt-2 w-full flex justify-between">
			<button
				{disabled}
				class="btn btn-sm hover-ripple"
				on:click|preventDefault={clearPad}>Clear</button
			>
			<button
				{disabled}
				class="btn btn-sm hover-ripple"
				on:click|preventDefault={() => (signByTyping = !signByTyping)}
			>
				Sign By Typing
			</button>
		</div>
	{/if}
</div>
